import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { RouteLocationNamedRaw } from 'vue-router'

import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { useAppStore } from '@/store/app-store'
import type { MainCategoriesType, ShortGameType } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'
import { isCountryRestricted } from '@/utils/requests/utils'

import {
    buildTwoPageCategoryRoute,
    getTwoPageCatalogHRUs,
    getTwoPageCategoryByHru,
    getTwoPageGameCategoryInfo,
    mapGameToTwoPageCard,
} from './two-page-data'

const BATCH_SIZE = 24
const MAX_GAMES_PER_SECTION = 8

export type TwoPageCatalogGame = ShortGameType & {
    slug: string
    categoryTitle: string
    mainCategory: string
    restricted_countries?: string[]
}

export type TwoPageCatalogSection = {
    title: string
    mainCategory: string
    categoryId: MainCategoriesType
    href?: RouteLocationNamedRaw
    games: TwoPageCatalogGame[]
    allGames: TwoPageCatalogGame[]
}

async function mapInBatches<T, R>(items: T[], mapper: (item: T) => Promise<R>): Promise<R[]> {
    const result: R[] = []

    for (let index = 0; index < items.length; index += BATCH_SIZE) {
        const batch = items.slice(index, index + BATCH_SIZE)
        const batchResult = await Promise.all(batch.map(mapper))
        result.push(...batchResult)
    }

    return result
}

export function useTwoPageCatalogData() {
    const { currentLocale } = useI18nAddon()
    const { getGameByParam } = useAPI()
    const appStore = useAppStore()

    const queryKey = computed(() => ['two-page-catalog', currentLocale.value, appStore.clientType])

    const { data, suspense } = useQuery({
        queryKey,
        queryFn: async () => {
            const cards = await mapInBatches(
                getTwoPageCatalogHRUs(),
                async (hru): Promise<TwoPageCatalogGame | null> => {
                    const result = await getGameByParam({
                        hru,
                        locale: currentLocale.value,
                        context: 'portal',
                        clientType: appStore.clientType,
                    })

                    if (result.status !== 'ok') {
                        return null
                    }

                    const category = getTwoPageGameCategoryInfo(result.data.game.hru)

                    return {
                        ...mapGameToTwoPageCard(result.data.game),
                        categoryTitle: category?.title || 'Puzzle',
                        restricted_countries: result.data.game.restricted_countries,
                    }
                },
            )

            return cards.filter((game): game is TwoPageCatalogGame => !!game)
        },
        persister: usePersisterFn(Persisters.HighCardinality),
    })

    const games = computed<TwoPageCatalogGame[]>(() => {
        const allGames = data.value || []
        return allGames.filter((game) => !isCountryRestricted(game.restricted_countries, appStore.countryCode))
    })
    const allSections = computed<TwoPageCatalogSection[]>(() => {
        const groupedGames = new Map<
            string,
            {
                title: string
                mainCategory: string
                categoryId: MainCategoriesType
                href?: RouteLocationNamedRaw
                games: TwoPageCatalogGame[]
            }
        >()

        for (const game of games.value) {
            const categoryGames = groupedGames.get(game.mainCategory)

            if (categoryGames) {
                categoryGames.games.push(game)
            } else {
                groupedGames.set(game.mainCategory, {
                    title: game.categoryTitle,
                    mainCategory: game.mainCategory,
                    categoryId: getTwoPageCategoryByHru(game.mainCategory)?.categoryId || 'puzzle',
                    href: buildTwoPageCategoryRoute(game.mainCategory),
                    games: [game],
                })
            }
        }

        return Array.from(groupedGames.values()).map((section) => ({
            title: section.title,
            mainCategory: section.mainCategory,
            categoryId: section.categoryId,
            href: section.href,
            games: section.games.slice(0, MAX_GAMES_PER_SECTION),
            allGames: section.games,
        }))
    })
    const sections = computed<TwoPageCatalogSection[]>(() => allSections.value)

    return {
        games,
        allSections,
        sections,
        preloadCatalogData: suspense,
    }
}
